import * as yup from 'yup';
import moment from 'moment';

export const validateOriginDate = (scheduled_time, destinations = [], validateRetroactive) => {
  const invalid_date = destinations.some((item) => {
    const date = moment(item.scheduledTime || item.scheduled_time, 'DD/MM/YYYY HH:mm');
    return moment(date).isBefore(scheduled_time);
  });

  if (validateRetroactive) {
    const is_retroactive_date = moment(scheduled_time).isBefore(moment());
    if (is_retroactive_date) {
      return 'Data máxima da coleta informada não pode ser anterior ao dia/horário atual.';
    }
  }

  if (invalid_date) {
    return 'Data de origem da coleta maior que alguma data de destino.';
  }

  return '';
};

export const validateDestinationDate = (scheduled_time, origins = [], validateRetroactive) => {
  const invalid_date = origins.some((item) => {
    const date = moment(item.scheduled_time || item.scheduledTime, 'DD/MM/YYYY HH:mm');
    return moment(scheduled_time).isBefore(date);
  });

  if (validateRetroactive) {
    const is_retroactive_date = moment(scheduled_time).isBefore(moment());
    if (is_retroactive_date) {
      return 'Data máxima do destino informada não pode ser anterior ao dia/horário atual.';
    }
  }

  if (invalid_date) {
    return 'Data do destino menor que a data de origem.';
  }

  return '';
};

export const addressSchema = yup.object().shape({
  address: yup.string().nullable().required('Logradouro obrigatório'),
  number: yup.string().nullable(),
  zip_code: yup.string().nullable(),
  city: yup
    .object({
      name: yup.string().required('Nome da cidade obrigatório'),
    })
    .nullable()
    .required('Cidade obrigatória'),
  province: yup.string().required('UF obrigatória'),
  country: yup.mixed().required('País obrigatório'),
  lat: yup
    .number()
    .typeError('Selecione um logradouro utilizando o seletor do google')
    .required('Selecione um logradouro utilizando o seletor do google'),
  lng: yup
    .number()
    .typeError('Selecione um logradouro utilizando o seletor do google')
    .required('Selecione um logradouro utilizando o seletor do google'),
  start_schedule: yup
    .string()
    .required('Data início obrigatória')
    .when('$validateRetroactive', {
      is: true,
      then: (schema) =>
        schema
          .test(
            'is-not-in-the-past',
            'Data de início não pode ser anterior ao dia atual.',
            (value) => {
              if (!value) return false;
              const inputDate = moment(value);
              const today = moment().startOf('day');
              return inputDate.isSameOrAfter(today);
            },
          )
          .test(
            'is-30-mins-ahead',
            'Data de início deve ser no mínimo 30 minutos após o horário atual.',
            (value) => {
              if (!value) return false;
              const inputDate = moment(value);
              return inputDate.isSameOrAfter(moment().add(30, 'minutes'));
            },
          ),
    }),
  scheduled_time: yup
    .string()
    .required('Data máxima obrigatória')
    .test('data-maior', 'Data máxima deve ser maior ou igual à data de início', function (value) {
      const { start_schedule } = this.parent;
      return moment(value).isSameOrAfter(moment(start_schedule));
    }),
  type: yup.mixed().when('$isFirst', {
    is: false,
    then: (schema) => schema.required('Tipo obrigatório'),
    otherwise: (schema) => schema.notRequired(),
  }),
});

export async function validateAddressAll(
  data,
  { isFirst = false, origins = [], destinations = [], validateRetroactive },
) {
  try {
    await addressSchema.validate(data, {
      abortEarly: false,
      context: { isFirst, validateRetroactive },
    });

    const scheduled_time = moment(data.scheduled_time, 'YYYY-MM-DDTHH:mm');
    const extraError = isFirst
      ? validateOriginDate(scheduled_time, destinations, validateRetroactive)
      : validateDestinationDate(scheduled_time, origins, validateRetroactive);

    if (extraError) {
      return {
        isValid: false,
        field: 'scheduled_time',
        errors: { scheduled_time: extraError },
      };
    }

    return { isValid: true };
  } catch (err) {
    const formErrors = {};
    (err.inner || []).forEach((error) => {
      const key = error.path.startsWith('city.') ? 'city' : error.path;
      if (!formErrors[key]) {
        formErrors[key] = error.message;
      }
    });
    return {
      isValid: false,
      errors: formErrors,
    };
  }
}





export function validateWaypointLimit(points = []) {
  const waypoints = points.slice(1, -1);

  if (waypoints.length > 25) {
    return {
      isValid: false,
      message: 'Não é permitido ultrapassar 25 pontos no mapa.',
    };
  }

  return {
    isValid: true,
    message: '',
  };
}

export function validateRequiredPoints(points = []) {
  const [origin, ...destinations] = points;

  if (!origin) {
    return {
      isValid: false,
      message: 'A rota precisa ter uma origem.',
    };
  }

  if (destinations.length === 0) {
    return {
      isValid: false,
      message: 'A rota precisa ter pelo menos um destino.',
    };
  }

  return {
    isValid: true,
    message: '',
  };
}

export function validateRequiredPointsForLoad({ origin, destinations }) {
  const isLatValid = typeof origin?.lat === 'number' && !isNaN(origin.lat);
  const isLngValid = typeof origin?.lng === 'number' && !isNaN(origin.lng);

  if (!origin || !isLatValid || !isLngValid) {
    return {
      isValid: false,
      message: 'A carga precisa ter uma origem válida.',
    };
  }

  const hasValidDestinations =
    Array.isArray(destinations) &&
    destinations.length > 0 &&
    destinations.every(
      (dest) =>
        typeof dest.lat === 'number' &&
        !isNaN(dest.lat) &&
        typeof dest.lng === 'number' &&
        !isNaN(dest.lng),
    );

  if (!hasValidDestinations) {
    return {
      isValid: false,
      message: 'A carga precisa ter pelo menos um destino válido.',
    };
  }

  return {
    isValid: true,
    message: '',
  };
}

export function validateSequentialDates(points = []) {
  const invalidIndexes = [];

  if (!points || points.length === 0) return invalidIndexes;

  for (let i = 1; i < points.length; i++) {
    const prev = points[i - 1];
    const current = points[i];

    const prevScheduledTime = prev?.scheduled_time || prev?.scheduledTime;
    const currentStartDate = current?.start_schedule || current?.startSchedule;

    if (prevScheduledTime && currentStartDate) {
      const momentPrevScheduledTime = moment(prevScheduledTime);
      const momentCurrentStartDate = moment(currentStartDate);
      if (
        momentPrevScheduledTime.isValid() &&
        momentCurrentStartDate.isValid() &&
        momentCurrentStartDate.isBefore(momentPrevScheduledTime)
      ) {
        invalidIndexes.push(i);
      }
    }
  }

  return invalidIndexes;
}
